import {
    ReactElement,
    useEffect,
    useLayoutEffect,
    useMemo,
    useState,
} from 'react';
import HistoryTable from './components/HistoryTable';
import { HeadTag } from 'shared/components';
import { UploadService } from 'services';
import { Toast, Utils } from 'shared/helpers';
import { formattingAction } from 'shared/helpers/FormatAction';
import { useDispatch, useSelector } from 'react-redux';
import { SocketActions } from 'state/socket/Action';
import { useMountedState } from 'react-use';
import { setHistoryRowsPerPageNumber } from 'state/persistapplication/Action';
import { AxiosError } from 'axios';

const PacketSessionsHistoryPage = (): ReactElement => {
    const dispatch = useDispatch();
    const isMounted = useMountedState();
    const [uploadHistory, setUploadHistory] = useState<any[]>([]);
    const [isRefreshHistory, setRefreshHistory] = useState<boolean>(false);
    const [page, setPage] = useState<number>(0);
    const [totalRows, setTotalRows] = useState<number>(0);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isNetworkError, setIsNetworkError] = useState<boolean>(false);
    const socketState = useSelector((state: any) => state.socketState);
    const persistRowsPerPage = useSelector(
        (state: any) => state.persistState.rowsPerPage.history
    );
    const [rowsPerPage, setRowsPerPage] = useState<number>(persistRowsPerPage);

    useLayoutEffect(() => {
        dispatch(setHistoryRowsPerPageNumber(rowsPerPage));
    }, [rowsPerPage]);

    useEffect(() => {
        if (isMounted() && persistRowsPerPage !== rowsPerPage) {
            setRowsPerPage(persistRowsPerPage);
        }
    }, []);
    /**
     * Fetches the upload history with the given offset.
     *
     * @param {number} offset - The offset for pagination.
     * @returns {Promise<void>} - A promise that resolves once the history is fetched.
     */
    const fetchHistory = async (
        offset: number,
        rowsperPage: number
    ): Promise<void> => {
        try {
            const response = await UploadService.uploadHistory({
                offset,
                limit: rowsperPage,
            });
            const uploadHistory = Array.from(
                response.data.data[0].paginatedResult
            ).map((o: any) => ({
                ...o,
                packets: Array.from(o.packets).map((el: any) => ({
                    ...el,
                    actions: formattingAction(el),
                })),
                actions: formattingAction(o),
            }));
            setIsNetworkError(false);
            setUploadHistory(() => uploadHistory);
            setTotalRows(response.data.data[0].totalCount[0]?.count ?? 0);
        } catch (error: any) {
            if (
                error !== null &&
                error instanceof AxiosError &&
                error.code === 'ERR_NETWORK'
            ) {
                setUploadHistory(() => []);
                setIsNetworkError(true);
            } else {
                setUploadHistory(() => []);
                setTotalRows(0);
                setIsNetworkError(false);
            }
            Utils.handleErrorResponse(error);
        }
    };

    /**
     * Fetches upload history.
     */
    useEffect(() => {
        (async () => {
            try {
                await fetchHistory(page + 1, rowsPerPage);
                setIsLoading(false);
            } catch (error) {
                Toast.showErrorMessage('Failed load history');
            }
        })();
    }, [page, rowsPerPage]);

    /**
     * Fetches upload history.
     */
    useEffect(() => {
        if (isRefreshHistory) {
            (async () => {
                try {
                    await fetchHistory(page + 1, rowsPerPage);
                } catch (error) {
                    Toast.showErrorMessage('Failed load history');
                }
            })();

            setRefreshHistory(false);
        }
    }, [isRefreshHistory]);

    const socketEvent = useMemo(
        () => socketState.socketData.eventName,
        [socketState.socketData.eventName]
    );

    useEffect(() => {
        if (socketEvent !== null) {
            if (socketEvent === 'uploadStatus') {
                setRefreshHistory(true);
            }
            dispatch(SocketActions.ResetSocketEvent({}));
        }
    }, [socketEvent]);
    return (
        <>
            <HeadTag title="Upload Sessions History" />
            <HistoryTable
                rowsPerPage={rowsPerPage}
                uploadHistory={uploadHistory}
                page={page}
                setPage={setPage}
                totalRows={totalRows}
                isLoading={isLoading}
                setIsLoading={setIsLoading}
                setRowsPerPage={setRowsPerPage}
                isNetworkError={isNetworkError}
            />
        </>
    );
};

export default PacketSessionsHistoryPage;
